import { useEffect, useState } from "react";
import { Link2, Plus, Package, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * Guardia Lotti + Match suggestions per una riga DDT.
 *
 * Regole (definite dal proprietario del negozio):
 *   Regola 1 (silenziosa)  — expirySource="estimated" + stesso batch di un lotto già
 *     esistente ma con scadenza diversa → adottiamo silenziosamente la scadenza del
 *     lotto esistente (l'adozione avviene lato backend: expiry_source viene passato
 *     a adjust-stock). Qui la UI mostra solo l'outcome "si somma al lotto X".
 *   Regola 3-4 (Warning A) — expirySource="read" + stesso batch di un lotto esistente
 *     ma con scadenza diversa → mostriamo warning con scelta "Fondi (default)" vs
 *     "Tieni separate".
 *   Regola 5 (Warning B)   — batch a distanza di Levenshtein == 1 da un lotto esistente
 *     (probabile refuso OCR) → warning senza default con scelta "Fondi" vs "Crea nuovo".
 *
 * Props:
 *  - name, category, batch, expiryDate: fields to match against
 *  - expirySource: "document"|"manual"|"ai_suggested"|"category_default"|"learned"|"missing"
 *  - linkedId: currently linked existing product id (if any)
 *  - onLink(existingProduct): user picked an existing product
 *  - onCreate(): user chose to create a new product (clears linkedId)
 *  - onAdoptLotFields({ batch?, expiry_date? }): sovrascrive campi della riga
 *    (usato quando l'utente accetta di fondere in un lotto esistente in Warning A/B)
 */

// Levenshtein distance (piccolo, batch corti). Case-insensitive, trimmato.
function levenshtein(a, b) {
  const s = (a || "").trim().toLowerCase();
  const t = (b || "").trim().toLowerCase();
  if (s === t) return 0;
  if (!s.length) return t.length;
  if (!t.length) return s.length;
  const m = s.length, n = t.length;
  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

const READ_SOURCES = new Set(["document", "manual"]);

export default function MatchSuggestions({
  name, category, batch, expiryDate, expirySource,
  linkedId, onLink, onCreate, onAdoptLotFields,
}) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoLinkedId, setAutoLinkedId] = useState(null);
  // Ricorda le decisioni prese dall'utente (per non ri-mostrare la stessa warning)
  const [dismissed, setDismissed] = useState({ warnA: null, warnB: null });

  useEffect(() => {
    if (!name || name.length < 3) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .matchProducts({ name, category, batch, min_similarity: 0.85 })
      .then((d) => {
        if (!cancelled) setMatches(d.matches || []);
      })
      .catch(() => {
        if (!cancelled) setMatches([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [name, category, batch]);

  // Aggancio automatico: similarity >= 95% è sufficiente.
  useEffect(() => {
    if (!matches.length || linkedId) return;
    const best = matches.find((m) => m.similarity >= 0.95);
    if (best && best.product?.id && autoLinkedId !== best.product.id) {
      setAutoLinkedId(best.product.id);
      onLink(best.product);
    }
  }, [matches, linkedId]);

  const linkedProduct = linkedId ? matches.find((m) => m.product?.id === linkedId)?.product : null;
  const isRead = READ_SOURCES.has(expirySource);
  const incomingBatch = (batch || "").trim();
  const incomingBatchLc = incomingBatch.toLowerCase();
  const incomingExpiry = expiryDate || null;

  // Rileva conflitti sui lotti del prodotto collegato
  const conflicts = (() => {
    if (!linkedProduct || !incomingBatch) return { sameBatchDiffExpiry: null, similarBatch: null };
    const lots = (linkedProduct.lots || []).filter(
      (l) => (l.batch || "").trim() !== "" || l.expiry_date
    );
    let sameBatchDiffExpiry = null;
    let similarBatch = null;
    for (const l of lots) {
      const lb = (l.batch || "").trim().toLowerCase();
      if (!lb) continue;
      if (lb === incomingBatchLc) {
        if ((l.expiry_date || null) !== incomingExpiry) {
          sameBatchDiffExpiry = l;
        }
      } else if (levenshtein(lb, incomingBatchLc) === 1) {
        // Prendi il primo (più vicino in ordine di comparsa)
        if (!similarBatch) similarBatch = l;
      }
    }
    return { sameBatchDiffExpiry, similarBatch };
  })();

  // Predice cosa succederà al salvataggio DDT (per la label informativa)
  const outcomeForLinked = (() => {
    if (!linkedProduct) return null;
    const lots = linkedProduct.lots || [];
    // Se stiamo per adottare silenziosamente (Regola 1) o l'utente ha già fuso in un lotto
    // via warning: mostra "si somma al lotto X"
    if (conflicts.sameBatchDiffExpiry && !isRead) {
      const l = conflicts.sameBatchDiffExpiry;
      return { kind: "accumulate", label: l.batch ? `Lotto ${l.batch}` : "Lotto senza numero", silent: true };
    }
    // Match esatto batch+expiry
    const matchLot = lots.find(
      (l) => ((l.batch || "").trim().toLowerCase() === incomingBatchLc)
             && ((l.expiry_date || null) === incomingExpiry)
    );
    if (matchLot) {
      const label = matchLot.batch ? `Lotto ${matchLot.batch}` : "Lotto senza numero";
      return { kind: "accumulate", label };
    }
    return { kind: "new_lot" };
  })();

  // === Warning A: batch identico ma scadenza diversa (READ da documento/manuale) ===
  // Default: fondi. Chiave per dismiss: batch+expiry incoming
  const warnAKey = conflicts.sameBatchDiffExpiry && isRead
    ? `${incomingBatchLc}|${incomingExpiry || ""}` : null;
  const showWarnA = warnAKey && dismissed.warnA !== warnAKey;

  // === Warning B: batch a distanza 1 da un lotto esistente (refuso OCR) ===
  // Nessun default. Chiave per dismiss: incoming batch + candidate lot id
  const warnBKey = conflicts.similarBatch && !conflicts.sameBatchDiffExpiry
    ? `${incomingBatchLc}|${conflicts.similarBatch.id || conflicts.similarBatch.batch}` : null;
  const showWarnB = warnBKey && dismissed.warnB !== warnBKey;

  // Handlers
  const acceptMergeA = () => {
    if (!conflicts.sameBatchDiffExpiry) return;
    onAdoptLotFields?.({ expiry_date: conflicts.sameBatchDiffExpiry.expiry_date });
    setDismissed((d) => ({ ...d, warnA: warnAKey }));
  };
  const keepSeparateA = () => setDismissed((d) => ({ ...d, warnA: warnAKey }));

  const acceptMergeB = () => {
    if (!conflicts.similarBatch) return;
    onAdoptLotFields?.({
      batch: conflicts.similarBatch.batch,
      expiry_date: conflicts.similarBatch.expiry_date,
    });
    setDismissed((d) => ({ ...d, warnB: warnBKey }));
  };
  const createNewB = () => setDismissed((d) => ({ ...d, warnB: warnBKey }));

  if (loading) {
    return (
      <div className="text-[11px] text-[#8E918C] italic">Controllo corrispondenze...</div>
    );
  }
  if (!matches.length) return null;

  return (
    <div className="mt-2 p-3 rounded-lg bg-[#FDF6E3] border border-[#F2DEB0] space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#8B6518]">
        <Link2 size={12} />
        {matches.length} possibile corrispondenza già in magazzino
      </div>

      <div className="space-y-1.5">
        {matches.map((m) => {
          const isLinked = linkedId === m.product.id;
          return (
            <div
              key={m.product.id}
              data-testid={`match-candidate-${m.product.id}`}
              className={`flex flex-wrap items-center gap-2 p-2 rounded-md border transition-all ${
                isLinked
                  ? "bg-[#E8F3EC] border-[#2E4A3B]"
                  : "bg-white border-[#F2DEB0] hover:border-[#8B6518]"
              }`}
            >
              <div className="min-w-0 flex-1 basis-full sm:basis-0">
                <div className="text-sm font-medium break-words [overflow-wrap:anywhere]">{m.product.name}</div>
                <div className="text-[11px] text-[#8E918C] flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                  <span>Stock: {m.product.stock}</span>
                  {m.product.batch && <span className="font-mono break-all">Lotto: {m.product.batch}</span>}
                  <span className="text-[#8B6518] font-medium">{Math.round(m.similarity * 100)}% simile</span>
                  {m.same_batch && (
                    <span className="text-[#2B5A3F] font-semibold">· stesso lotto</span>
                  )}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant={isLinked ? "default" : "outline"}
                onClick={() => (isLinked ? onCreate() : onLink(m.product))}
                data-testid={`match-link-${m.product.id}`}
                className={
                  (isLinked
                    ? "bg-[#2E4A3B] hover:bg-[#21382C] text-white "
                    : "border-[#8B6518] text-[#8B6518] hover:bg-[#FDF6E3] ") +
                  "text-xs h-7 w-full sm:w-auto"
                }
              >
                {isLinked ? "Collegato ✓" : "Usa questo"}
              </Button>
            </div>
          );
        })}
      </div>

      {/* === Warning A: stesso batch, scadenza diversa (READ) === */}
      {showWarnA && (
        <div
          data-testid="lot-guard-warning-same-batch"
          className="p-2.5 rounded-md bg-[#FBE9E7] border border-[#E88A6E] space-y-2"
        >
          <div className="flex items-start gap-1.5 text-xs text-[#8B2E1A]">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <div className="min-w-0 break-words">
              Il lotto <span className="font-mono font-semibold">{conflicts.sameBatchDiffExpiry.batch}</span> esiste
              già con scadenza <span className="font-semibold">{conflicts.sameBatchDiffExpiry.expiry_date || "—"}</span>.
              Nuova scadenza letta: <span className="font-semibold">{incomingExpiry || "—"}</span>. Sono la stessa partita?
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              size="sm"
              onClick={acceptMergeA}
              data-testid="lot-guard-warnA-merge"
              className="h-7 text-xs bg-[#2E4A3B] hover:bg-[#21382C] text-white"
            >
              Fondi con lotto esistente
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={keepSeparateA}
              data-testid="lot-guard-warnA-keep"
              className="h-7 text-xs border-[#8B2E1A] text-[#8B2E1A] hover:bg-[#FBE9E7]"
            >
              Tieni separate
            </Button>
          </div>
        </div>
      )}

      {/* === Warning B: batch a distanza 1 (probabile refuso OCR) === */}
      {showWarnB && (
        <div
          data-testid="lot-guard-warning-similar-batch"
          className="p-2.5 rounded-md bg-[#FDF6E3] border border-[#D9A64A] space-y-2"
        >
          <div className="flex items-start gap-1.5 text-xs text-[#7A5410]">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <div className="min-w-0 break-words">
              Forse è il lotto <span className="font-mono font-semibold">{conflicts.similarBatch.batch}</span> già in
              magazzino? Se hai già ricevuto questa merce e non l&apos;hai ancora venduta,
              probabilmente sono la stessa partita.
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              size="sm"
              onClick={acceptMergeB}
              data-testid="lot-guard-warnB-merge"
              variant="outline"
              className="h-7 text-xs border-[#7A5410] text-[#7A5410] hover:bg-[#FDF6E3]"
            >
              Fondi con {conflicts.similarBatch.batch}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={createNewB}
              data-testid="lot-guard-warnB-create"
              variant="outline"
              className="h-7 text-xs border-[#7A5410] text-[#7A5410] hover:bg-[#FDF6E3]"
            >
              Crea nuovo lotto
            </Button>
          </div>
        </div>
      )}

      {linkedId && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[#F2DEB0]">
          <Package size={12} className="text-[#2E4A3B] flex-shrink-0" />
          <span
            data-testid="match-outcome-label"
            className="text-[11px] text-[#2B5A3F] font-medium min-w-0 flex-1 break-words"
          >
            {outcomeForLinked?.kind === "accumulate"
              ? `Al salvataggio la merce si somma al ${outcomeForLinked.label} già in magazzino${outcomeForLinked.silent ? " (scadenza adottata dal lotto esistente)" : ""}.`
              : outcomeForLinked?.kind === "new_lot"
              ? "Al salvataggio verrà aggiunto un nuovo lotto a questo prodotto."
              : "Al salvataggio verrà aggiornato lo stock del prodotto collegato."}
          </span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCreate}
            data-testid="match-reset-btn"
            className="text-[11px] h-6 text-[#8B6518] hover:bg-transparent underline"
          >
            <Plus size={10} className="mr-0.5" />
            Crea nuovo prodotto
          </Button>
        </div>
      )}
    </div>
  );
}

// Export interno per test unitari se serviranno
export { levenshtein };
